// Catalog.ID Client — Where each identity's sessions live
// ============================================================================
// Every session is created at the account's ORIGIN node (docs/cid-governance.md
// D18, M7a). This client therefore talks to one API server PER IDENTITY:
//
//   * the ENTRY node is the node this client starts from — the page's own node
//     standalone (`getApiBase()` is ''), the helper's runtime-config in the
//     desktop;
//   * an identity's ORIGIN is found by asking the entry node
//     (`GET /api/account/{cid}/origin`, anonymous) and remembered here beside the
//     identity's session token, keyed by CID like everything else on the device.
//
// ADDRESSES (apps/docs/plans/operator-codes.md §7.5). Standalone, a node's
// answer is the address. In the desktop it never is: the answer names the
// origin's CODE, and the address is the one the desktop's VERIFIED trust-root
// directory gives that code (`getCidNodes()`, via the helper's runtime-config).
// A code the directory does not run cid at — after one re-poll — has no
// address: the identity is unreachable. A remembered origin is resolved by its
// code again at every use.
//
// There is NO top-level navigation to another node: in the desktop that would
// leave the helper's origin and lose dashboard detection, the key-grant relay
// and the device keys, and standalone it would lose this page's localStorage.
// The API server is switched instead — cross-origin calls, which the nodes'
// CORS allows for `*.catalog.id` and loopback.
//
// Stored per identity: `{cid}_origin` = JSON `{ code?, url }`. An EMPTY `url`
// means "the entry node itself" — what the lookup answers with `self: true`
// standalone, and the only address an uncertified node has. (In the desktop the
// entry node follows the signed-in session, so there the directory address is
// stored instead.) An identity with nothing stored is treated as living at the
// entry node until a lookup or an `origin_only` answer says otherwise.
// ============================================================================

import { cidNodeUrl, getApiBase, getCidNodes, refreshCidNodes, resolveNodeHost } from './dashboard-context';

/** Where one identity's sessions are created. */
export interface IdentityOrigin {
    /** The origin's operator code, when known (for display). */
    code?: string;
    /** The origin's base URL, no trailing slash; '' = the entry node itself. */
    url: string;
}

const ORIGIN_SUFFIX = '_origin';

function originKey(cid: string): string { return `${cid}${ORIGIN_SUFFIX}`; }

/** The remembered origin of `cid`, or null when none is on record. */
export function getStoredOrigin(cid: string): IdentityOrigin | null {
    let raw: string | null = null;
    try { raw = localStorage.getItem(originKey(cid)); } catch { return null; }
    if (raw === null) return null;
    try {
        const parsed = JSON.parse(raw) as Partial<IdentityOrigin>;
        if (typeof parsed.url !== 'string') return null;
        return typeof parsed.code === 'string' ? { code: parsed.code, url: parsed.url } : { url: parsed.url };
    } catch {
        return null;
    }
}

/** Remember where `cid`'s sessions are created. */
export function rememberOrigin(cid: string, origin: IdentityOrigin): void {
    const value: IdentityOrigin = { url: origin.url.replace(/\/+$/, '') };
    if (origin.code) value.code = origin.code;
    localStorage.setItem(originKey(cid), JSON.stringify(value));
}

/** Forget `cid`'s origin (the identity leaves this device). */
export function forgetOrigin(cid: string): void {
    localStorage.removeItem(originKey(cid));
}

/**
 * The API base for requests made for `cid`: its origin's URL, or the entry
 * node's when the origin IS the entry node, is unknown, or no identity is named.
 * In the desktop the origin's code is resolved through the directory at use;
 * the stored URL stands only for a code the directory no longer lists (the
 * picker greys that identity — {@link originAvailability}).
 */
export function apiBaseFor(cid: string | null | undefined): string {
    if (!cid) return getApiBase();
    const origin = getStoredOrigin(cid);
    if (!origin) return getApiBase();
    if (getCidNodes() !== null && origin.code) {
        return cidNodeUrl(origin.code) ?? (origin.url || getApiBase());
    }
    return origin.url ? origin.url : getApiBase();
}

/** Whether `origin` is another node than the entry node this page talks to. */
export function originIsElsewhere(origin: IdentityOrigin): boolean {
    return Boolean(origin.url) && origin.url !== getApiBase();
}

// ── addresses ───────────────────────────────────────────────────────────────

/** Re-poll the directory after a miss; injectable for tests. */
type DirectoryRefresh = () => Promise<void>;

const defaultRefresh: DirectoryRefresh = () => refreshCidNodes();

/** The directory's address for `code`, asking the helper to re-poll once on a miss; null when it has none. */
async function directoryAddress(code: string, refresh: DirectoryRefresh): Promise<string | null> {
    const known = cidNodeUrl(code);
    if (known) return known;
    try { await refresh(); } catch { /* what is known stands */ }
    return cidNodeUrl(code) ?? null;
}

/**
 * The address of the origin a node's answer names — by `code` and the URL the
 * node gave. In the desktop: the directory's address for the code (the node's
 * URL is ignored), or null when the directory does not run cid there.
 * Standalone: the URL the node answered with ('' = the entry node itself).
 */
export async function originAddress(
    code: string, nodeUrl: string, refresh: DirectoryRefresh = defaultRefresh,
): Promise<string | null> {
    if (getCidNodes() !== null) return directoryAddress(code, refresh);
    return nodeUrl.replace(/\/+$/, '');
}

// ── the lookup ──────────────────────────────────────────────────────────────

export type OriginLookup =
    /** The origin answers at `origin.url` ('' = the entry node itself). */
    | { kind: 'found'; origin: IdentityOrigin }
    /** The origin's code holds no active certificate (in the desktop: the directory
     *  does not run cid there): no address exists for it. */
    | { kind: 'unreachable'; code: string }
    /** No such CID. */
    | { kind: 'unknown' };

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/**
 * Ask the ENTRY node where `cid`'s origin is. Anonymous — no session token is
 * sent. Throws when the entry node cannot be asked at all (network, 5xx other
 * than `origin_unreachable`), which is a different failure from any answer.
 * In the desktop the answer's CODE is mapped through the directory
 * ({@link originAddress}); the URL in the answer is ignored.
 */
export async function lookupOrigin(
    cid: string,
    fetchImpl: FetchLike = (i, init) => fetch(i, init),
    refresh: DirectoryRefresh = defaultRefresh,
): Promise<OriginLookup> {
    const resp = await fetchImpl(`${getApiBase()}/api/account/${encodeURIComponent(cid)}/origin`, { method: 'GET' });
    let body: Record<string, unknown> | null = null;
    try { body = await resp.json() as Record<string, unknown>; } catch { body = null; }

    if (resp.ok && body && typeof body.code === 'string') {
        if (getCidNodes() !== null) {
            // The entry node's own members too: the entry follows the signed-in
            // session, so "here" would later mean another node.
            const url = await directoryAddress(body.code, refresh);
            return url ? { kind: 'found', origin: { code: body.code, url } } : { kind: 'unreachable', code: body.code };
        }
        const self = body.self === true;
        const url = typeof body.url === 'string' ? body.url : '';
        if (!self && !url) return { kind: 'unreachable', code: body.code };
        return { kind: 'found', origin: { code: body.code, url: self ? '' : url.replace(/\/+$/, '') } };
    }
    if (resp.status === 404) return { kind: 'unknown' };
    if (body && body.error === 'origin_unreachable' && typeof body.code === 'string') {
        return { kind: 'unreachable', code: body.code };
    }
    throw new Error(`The node could not say where this account lives (HTTP ${resp.status}).`);
}

/** {@link lookupOrigin}, remembering a found origin for `cid`. */
export async function resolveOrigin(cid: string, fetchImpl?: FetchLike, refresh?: DirectoryRefresh): Promise<OriginLookup> {
    const answer = await lookupOrigin(cid, fetchImpl, refresh);
    if (answer.kind === 'found') rememberOrigin(cid, answer.origin);
    return answer;
}

// ── reachability ────────────────────────────────────────────────────────────

/** How long the identity picker waits for an origin before greying it. */
export const ORIGIN_PROBE_TIMEOUT_MS = 3_000;

/**
 * Whether the node at `base` ('' = the entry node) answers its health check.
 * `/health` answers 503 when the node cannot serve (database unreachable, node
 * ended) — that is "unreachable" too. Never throws; bounded by a timeout, since
 * a dead loopback port can hang a connect indefinitely.
 */
export async function isNodeReachable(
    base: string, fetchImpl: FetchLike = (i, init) => fetch(i, init), timeoutMs = ORIGIN_PROBE_TIMEOUT_MS,
): Promise<boolean> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const resp = await fetchImpl(`${base}/health`, { method: 'GET', signal: controller.signal });
        return resp.ok;
    } catch {
        return false;
    } finally {
        clearTimeout(timer);
    }
}

/** Where an identity stands for the picker. */
export type OriginAvailability =
    | { available: true; origin: IdentityOrigin }
    | { available: false; code?: string };

/**
 * Can `cid` be used from here right now? Its remembered origin — or, when none
 * is on record, the entry node's answer — must answer its health check. An
 * origin that has no address (in the desktop: one the directory does not run
 * cid at), or does not answer, makes the identity unavailable: there is NO
 * fallback to another node (D18).
 */
export async function originAvailability(
    cid: string, fetchImpl?: FetchLike, refresh: DirectoryRefresh = defaultRefresh,
): Promise<OriginAvailability> {
    let origin = getStoredOrigin(cid);
    if (origin && origin.code && getCidNodes() !== null) {
        const url = await directoryAddress(origin.code, refresh);
        if (!url) return { available: false, code: origin.code };
        origin = { code: origin.code, url };
    }
    if (!origin) {
        let answer: OriginLookup;
        try {
            answer = await resolveOrigin(cid, fetchImpl, refresh);
        } catch {
            return { available: false };
        }
        if (answer.kind === 'unreachable') return { available: false, code: answer.code };
        if (answer.kind === 'unknown') return { available: false };
        origin = answer.origin;
    }
    const base = origin.url || getApiBase();
    return await isNodeReachable(base, fetchImpl)
        ? { available: true, origin }
        : { available: false, code: origin.code };
}

/**
 * The host (plus any non-root path base) of `cid`'s ORIGIN, for printed
 * material ("Open aaaa.catalog.id and choose …"): the sheets name the node that
 * holds the account, which need not be the node this client entered through.
 * See `resolveNodeHost`; undefined when nothing is known.
 */
export function nodeHostFor(cid: string | null | undefined): string | undefined {
    let locationHost = '';
    try { locationHost = window.location.host; } catch { /* no window (tests) */ }
    return resolveNodeHost(apiBaseFor(cid), locationHost);
}
